import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import { notificationSettings } from '@shopmy/universal';
import './RecipientSettingsPopover.scss';

const { settingAllowsChannel } = notificationSettings;

// Every switch on the recipient's Settings → Notifications page, grouped as that page groups them.
// Closes on a click anywhere outside its anchor; the anchor's own chip toggles it, so a click there is left alone.
const RecipientSettingsPopover = ({ groups, values, onClose }) => {
  const ref = useRef(null);
  useEffect(() => {
    const onClick = e => ref.current && !ref.current.parentElement.contains(e.target) && onClose();
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [onClose]);

  return (
    <div className='recipient-settings-popover' ref={ref}>
      <div className='popover-head'>
        <span className='popover-title'>All notification settings</span>
        <span className='col-label'>Push</span>
        <span className='col-label'>Email</span>
      </div>
      {groups.map(group => (
        <div key={group.title} className='popover-group'>
          <div className='group-title'>{group.title}</div>
          {group.options.map(option => {
            const value = values?.[option.setting];
            if (option.choices) {
              const chosen = option.choices.find(choice => choice.value === (value ?? option.defaultValue));
              return (
                <div key={option.setting} className='switch-row'>
                  <span className='switch-labels'>
                    <span className='switch-display'>{option.display}</span>
                    <code className='switch-setting'>{option.setting}</code>
                  </span>
                  <span className='choice-value'>{chosen ? chosen.label : value || '—'}</span>
                </div>
              );
            }
            const push = settingAllowsChannel(value, 'push');
            const email = settingAllowsChannel(value, 'email');
            return (
              <div key={option.setting} className={cn('switch-row', { off: !push && !email })}>
                <span className='switch-labels'>
                  <span className='switch-display'>{option.display}</span>
                  <code className='switch-setting'>{option.setting}</code>
                </span>
                <Mark on={push} />
                <Mark on={email} />
              </div>
            );
          })}
        </div>
      ))}
      <div className='popover-foot'>{values ? 'Mirrors their Settings → Notifications page. Read-only here.' : 'No settings row exists for this account, so every notification treats every switch as off.'}</div>
    </div>
  );
};

RecipientSettingsPopover.propTypes = {
  groups: PropTypes.array.isRequired,
  values: PropTypes.object,
  onClose: PropTypes.func.isRequired
};

const Mark = ({ on }) => <span className={cn('mark', { on })}>{on ? '✓' : '–'}</span>;
Mark.propTypes = { on: PropTypes.bool.isRequired };

export default RecipientSettingsPopover;
